/* eslint-disable react/style-prop-object */
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import InputRange from 'react-input-range';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import debounce from 'lodash/debounce';
import cx from 'classnames';
import {
  fromCents, getCurrencyCode, getMinimumFractionDigits, isMobile,
} from 'utils/helpers';

const propTypes = {
  defaultValue: PropTypes.number,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  step: PropTypes.number,
  name: PropTypes.string,
  productType: PropTypes.string,
  onChange: PropTypes.func,
  updateOnRender: PropTypes.bool,
  classNames: PropTypes.object,
  availablePrincipalsIndex: PropTypes.array,
  showMaxValue: PropTypes.bool,
  useDecimals: PropTypes.bool,
  alignLeft: PropTypes.bool,
  warning: PropTypes.bool,
  asterisk: PropTypes.bool,
  intl: PropTypes.object.isRequired,
};

class InputRangeSlider extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { value: props.defaultValue };
    this.onIncrementClick = this.onIncrementClick.bind(this);
    this.onDecrementClick = this.onDecrementClick.bind(this);
    this.onValueChange = this.onValueChange.bind(this);
    this.getValue = this.getValue.bind(this);
    this.getMaxValue = this.getMaxValue.bind(this);
    this.getMaxValueLabel = this.getMaxValueLabel.bind(this);
    this.onChangeComplete = debounce(this.onChangeComplete.bind(this), 50);
  }

  componentDidMount() {
    if (this.props.updateOnRender) {
      this.onChangeComplete();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.defaultValue !== nextProps.defaultValue
      || this.props.maxValue !== nextProps.maxValue
      || this.props.minValue !== nextProps.minValue
    ) {
      this.setState({ value: nextProps.defaultValue }, this.onChangeComplete);
    }
  }

  onValueChange(value) {
    this.setState({ value }, this.onChangeComplete);
  }

  onChangeComplete() {
    let value = this.state.value;
    if (this.props.productType === 'INSTALLMENT_maturity') {
      value = this.getValueFromIndex(value);
    }
    this.props.onChange(value);
  }

  onIncrementClick() {
    const { step } = this.props;
    let newValue = (Math.round((this.state.value + step) / step)) * step;
    newValue = Math.min(newValue, this.props.maxValue);
    if (newValue <= this.props.maxValue) {
      this.setState({ value: newValue }, this.onChangeComplete);
    }
  }

  onDecrementClick() {
    const { step } = this.props;
    const newValue = (Math.round((this.state.value - step) / step)) * step;
    if (newValue >= this.props.minValue) {
      this.setState({ value: newValue }, this.onChangeComplete);
    }
  }

  getValueFromIndex(index) {
    const { availablePrincipalsIndex } = this.props;
    if (availablePrincipalsIndex && availablePrincipalsIndex.length > 0) {
      return this.props.availablePrincipalsIndex[index];
    }
    return null;
  }

  getValue() {
    const { productType, availablePrincipalsIndex, useDecimals } = this.props;
    let value = this.state.value;

    if (availablePrincipalsIndex && availablePrincipalsIndex.length > 0) {
      switch (productType) {
        case 'INSTALLMENT':
        case 'CREDIT_LINE_firstDrawAmount':
        case 'CREDIT_LINE_principal':
        case 'INSTALLMENT_principal':
          value = fromCents(availablePrincipalsIndex[value]);
          break;
        default:
          value = availablePrincipalsIndex[value];
      }
    }

    if (productType === 'CREDIT_LINE_maturity' && ['EE', 'FI', 'LV', 'LT'].includes(COUNTRY)) {
      return (
        <FormattedMessage id="date.months.parameterized" values={{ value }} />
      );
    }

    if (productType === 'INSTALLMENT_maturity' || productType === 'INSTALLMENT_firstDrawAmount') {
      if (COUNTRY === 'LT') {
        return (
          <FormattedMessage id="date.months.parameterized" values={{ value }} />
        );
      }
      return <FormattedMessage id={`date.months.${value}`} />;
    }

    return (
      <FormattedNumber
        style="currency"
        currency={getCurrencyCode()}
        value={value}
        minimumFractionDigits={useDecimals ? getMinimumFractionDigits(value, 2) : 0}
        maximumFractionDigits={2}
      />
    );
  }

  getMaxValue() {
    const { maxValue, availablePrincipalsIndex } = this.props;

    if (availablePrincipalsIndex) {
      return availablePrincipalsIndex[maxValue];
    }

    return maxValue;
  }

  getMaxValueLabel() {
    const { productType, useDecimals, intl } = this.props;
    const maxValue = this.getMaxValue();
    let totalValue = maxValue;

    if (productType !== 'CREDIT_LINE_maturity') {
      totalValue = (
        intl.formatNumber(maxValue, {
          style: 'currency',
          currency: getCurrencyCode(),
          minimumFractionDigits: useDecimals ? getMinimumFractionDigits(maxValue, 2) : 0,
          maximumFractionDigits: 2,
        })
      );
    }

    return (
      <div className="input-range-control__max-value">
        <FormattedMessage
          id="slider.total"
          values={{
            total: totalValue,
          }}
        />
      </div>
    );
  }

  render() {
    const {
      showMaxValue, alignLeft, warning, asterisk,
    } = this.props;
    const headerClasses = cx({
      'input-range-control__header': true,
      'input-range-control__header--no-prefix-no-max-value': !showMaxValue,
      'input-range-control__header--align-left': alignLeft,
    });
    const valueNumberClasses = cx({
      'input-range-control__value-number': true,
      'input-range-control__value-number--warning': warning,
    });
    return (
      <div className="input-range-control">
        <div className={headerClasses}>
          <div className="input-range-control__value">
            <button
              onClick={this.onDecrementClick}
              className="input-range-control__value-btn input-range-control__value-btn--decrement"
              type="button"
            >
              <span className="input-range-control__value-btn-inner">&ndash;</span>
            </button>
            <span className={valueNumberClasses}>
              {this.getValue()}
              {asterisk ? '*' : ''}
            </span>
            <button
              onClick={this.onIncrementClick}
              className="input-range-control__value-btn input-range-control__value-btn--increment"
              type="button"
            >
              <span className="input-range-control__value-btn-inner">+</span>
            </button>
          </div>
          {showMaxValue && !isMobile && this.getMaxValueLabel()}
        </div>
        <div className="input-range-control__slider">
          <InputRange
            {...this.props}
            name={this.props.name}
            value={this.state.value}
            onChange={this.onValueChange}
            classNames={this.props.classNames}
          />
        </div>
        {showMaxValue && isMobile && this.getMaxValueLabel()}
      </div>
    );
  }
}

InputRangeSlider.propTypes = propTypes;

export default injectIntl(InputRangeSlider);
